import { Injectable } from '@angular/core';
import { Device, Call } from '@twilio/voice-sdk';
import { BehaviorSubject } from 'rxjs';
import { ToasterMessageService } from '../../core/services/topbar-toaster/toaster-message.service';
import { DashboardService } from './dashboard.service';
import { CommonHttpsService } from '../../core/services/https/common-https.service';

// Shape of the periodic stats Call emits via 'sample' — see @twilio/voice-sdk's
// RTCSample. Declared locally since the SDK doesn't export this path publicly.
interface RTCSample {
  codecName: string;
  jitter: number;
  mos: number | null;
  packetsLost: number;
  packetsLostFraction: number;
  rtt: number;
  timestamp: number;
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class TwilioService {
  private device?: Device;
  public device$ = new BehaviorSubject<Device | undefined>(undefined);
  public activeCall$ = new BehaviorSubject<Call | undefined>(undefined);
  private activeCall?: Call;
  private callData: {
    sid: string;
    duration: number;
  } = { sid: '', duration: 0 };
  public latestCallStats$ = new BehaviorSubject<any>(undefined);
  private callSamples: RTCSample[] = [];
  private callWarnings: { name: string; data: any; timestamp: number }[] = [];
  private callStartedAt = 0;

  constructor(
    public messageService: ToasterMessageService,
    private dashboardService: DashboardService,
    public commonhttpService: CommonHttpsService,
  ) {}

  setActiveCall(call: Call | undefined) {
    this.activeCall = call;
    this.activeCall$.next(call);
  }

  getActiveCall() {
    return this.activeCall;
  }

  getDevice() {
    return this.device;
  }

  getCallData() {
    return this.callData;
  }

  setCallData(sid: string, duration: number) {
    this.callData.sid = sid;
    this.callData.duration = duration;
  }

  sendDigit(digit: string) {
    if (this.activeCall) {
      console.log('Sending digit via TwilioService:', digit);
      this.activeCall.sendDigits(digit);
    } else {
      console.warn('No active call to send digit');
    }
  }

  toggleMute(mute: boolean) {
    if (this.activeCall) {
      console.log('Toggling mute state:', mute);
      this.activeCall.mute(mute);
    } else {
      console.warn('No active call to mute/unmute');
    }
  }

  initialize(token: any) {
    // Guard against redundant re-init: every page that needs a ready Device
    // (main-layout at login, profile-view on opening a patient) calls this.
    // Without this guard, each call created a brand-new Device on top of the
    // still-live previous one, which was never destroyed — leaking a
    // registered Device (its own signaling socket/heartbeat) per patient
    // visited in a shift. Harmless on a machine with CPU to spare, but the
    // accumulated background load was enough to degrade live call audio on
    // more resource-constrained sessions (e.g. VDI).
    if (
      this.device &&
      (this.device.state === 'registered' ||
        this.device.state === 'registering')
    ) {
      console.log('ℹ️ Twilio Device already initialized — skipping re-init');
      this.device$.next(this.device);
      return;
    }

    try {
      console.log('Creating Twilio Device...');
      const deviceOptions = {
        allowIncomingWhileBusy: true,
        // Prefer Opus (variable bitrate, better loss/jitter resilience) over PCMU
        codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
        // Cap Opus's bitrate so the call needs less bandwidth — lowers the
        // chance of the jitter buffer starving/overflowing on constrained or
        // variable connections. 16kbps is solid voice quality per Twilio's
        // recommended 8000-40000bps range for speech.
        maxAverageBitrate: 16000,
        // Mark outgoing RTP for QoS prioritization where the network honors DSCP.
        dscp: true,
        // Agents connecting via VDI capture mic audio through a virtual/redirected
        // device rather than real hardware. Chrome's default AGC/noise-suppression
        // filters are tuned for a real mic+speaker acoustic path and can distort an
        // already-processed virtual audio signal. Echo cancellation is left on since
        // disabling it risks audible echo for agents on speakers instead of headsets.
        rtcConstraints: {
          audio: {
            autoGainControl: false,
            noiseSuppression: false,
          },
        },
      };
      this.device = new Device(token, deviceOptions);
      // publish device so consumers can attach listeners when available
      this.device$.next(this.device);

      this.device.on('registering', () => {
        console.log('ℹ️ Registering Twilio Device...');
      });

      this.device.register();

      console.log(this.device);

      this.device.on('registered', () => {
        console.log('✅ Twilio Device registered');
        localStorage.setItem('deviceRegistered', 'true');
        // this.messageService.successMessage('Twilio Device registered', true);
      });

      this.device.on('incoming', (call: Call) => {
        console.log('📞 Incoming call:', call);
        // this.messageService.infoMessage('Incoming call', true);
      });

      this.device.on('error', (error: any) => {
        console.error('❌ Twilio Device error:', error);
        localStorage.setItem('deviceRegistered', 'false');
        // this.messageService.errroMessage('Twilio Device error', true);
      });

      // this.device.on('incoming', (call) => {
      //   console.log('📞 Incoming call');
      //   call.accept();
      // });

      this.device.on('ready', () => {
        console.log('✅ Twilio Device ready for calls');
        localStorage.setItem('deviceRegistered', 'true');
        // this.messageService.successMessage(
        //   '✅ Twilio Device ready for calls',
        //   true,
        // );
      });
    } catch (error) {
      console.error('🚫 Failed to initialize Twilio Device:', error);
      localStorage.setItem('deviceRegistered', 'false');
      this.device$.next(undefined);
      // this.messageService.errroMessage(
      //   '🚫 Failed to initialize Twilio Device',
      //   true,
      // );
    }
  }

  // device.connect() before the SDK's async 'registered' event has fired
  // silently fails to place the call — this waits (briefly) for that event
  // instead of trusting device truthiness alone.
  private waitForRegistration(timeoutMs = 8000): Promise<boolean> {
    return new Promise((resolve) => {
      if (!this.device) {
        resolve(false);
        return;
      }
      if (this.device.state === 'registered') {
        resolve(true);
        return;
      }

      const device = this.device;
      const onRegistered = () => {
        clearTimeout(timer);
        resolve(true);
      };
      const timer = setTimeout(() => {
        device.off('registered', onRegistered);
        resolve(false);
      }, timeoutMs);
      device.once('registered', onRegistered);
    });
  }

  async makeCall(
    params: Record<string, string> = {},
    patientId?: any,
    getConfID?: boolean,
  ): Promise<Call | undefined> {
    if (!this.device) {
      console.error('⚠️ Twilio Device is not initialized');
      return undefined;
    }

    if (this.device.state !== 'registered') {
      const registered = await this.waitForRegistration();
      if (!registered) {
        console.error('⚠️ Twilio Device did not finish registering in time');
        this.messageService.errroMessage(
          'Phone system is still connecting. Please wait a moment and try again.',
          true,
        );
        return undefined;
      }
    }

    try {
      console.log('📞 [TwilioService] Placing call — device.state:', this.device.state, 'params:', params);
      const call: Call = await this.device.connect({ params });
      console.log('📞 [TwilioService] device.connect() resolved — call.parameters:', (call as any)?.parameters, 'call.status():', call.status());

      this.callSamples = [];
      this.callWarnings = [];
      this.callStartedAt = Date.now();

      call.on('ringing', () => {
        console.log('Phone is ringing...');
      });

      call.on('accept', (func: any) => {
        console.log('✅ Call accepted', func);

        localStorage.setItem('callActive', 'true');
        // keep a reference to the active call so other components can access it
        this.activeCall = call;
        this.activeCall$.next(call);
        if (patientId) {
          // if (!getConfID) {
          //   return;
          // }
          // setTimeout(() => {
          //   this.dashboardService
          //     .GetConferenceId(patientId)
          //     .subscribe((data: any) => {
          //       if (typeof (this as any).onConferenceIdFetched === 'function') {
          //         (this as any).onConferenceIdFetched(data.conferenceId);
          //       }
          //     });
          // }, 3400);
        } else {
          console.warn('No patientId provided for GetConferenceId');
        }
        //https://twilioapidev.carevance.solutions/api/GetConferenceId?patientId=6666
      });

      call.on('disconnect', () => {
        localStorage.setItem('callActive', 'false');
        this.logCallSummary(call);
        // clear activeCall reference
        this.activeCall = undefined;
        this.activeCall$.next(undefined);
        this.latestCallStats$.next(undefined);
        this.device?.disconnectAll();
      });

      call.on('error', (err: any) => {
        localStorage.setItem('callActive', 'false');
        console.error('❗ Call error:', err);
        this.logCallSummary(call);
        this.activeCall = undefined;
        this.activeCall$.next(undefined);
        this.latestCallStats$.next(undefined);
      });

      call.on('warning', (warningName: string, warningData: any) => {
        console.warn(`⚠️ [TwilioCallWarning] ${warningName}`, warningData);
        this.callWarnings.push({
          name: warningName,
          data: warningData,
          timestamp: Date.now(),
        });
      });

      call.on('warning-cleared', (warningName: string) => {
        console.info(`✅ [TwilioCallWarning cleared] ${warningName}`);
      });

      call.on('sample', (sample: RTCSample) => {
        this.latestCallStats$.next(sample);
        this.callSamples.push(sample);
      });

      return call;
    } catch (error) {
      localStorage.setItem('callActive', 'false');
      console.error('🚫 Failed to make call:', error);
      return undefined;
    }
  }

  // Prints a call-quality report to the console when a call ends — averages
  // jitter/RTT/packet loss/MOS across every 'sample' tick plus every warning
  // that fired, so a specific bad call (e.g. a VDI test) can be diagnosed
  // from devtools/logs without needing Twilio Console access.
  private logCallSummary(call: Call): void {
    const durationSec = this.callStartedAt
      ? Math.round((Date.now() - this.callStartedAt) / 1000)
      : undefined;
    const samples = this.callSamples;

    if (!samples.length) {
      console.log(
        `📊 [CallSummary] callSid=${call.parameters?.['CallSid'] ?? 'unknown'} duration=${durationSec}s — no samples collected (call likely too short)`,
      );
      return;
    }

    const avg = (fn: (s: RTCSample) => number) =>
      Math.round(
        (samples.reduce((sum, s) => sum + fn(s), 0) / samples.length) * 100,
      ) / 100;
    const max = (fn: (s: RTCSample) => number) =>
      Math.max(...samples.map(fn));
    const mosValues = samples
      .map((s) => s.mos)
      .filter((m): m is number => m !== null && m !== undefined);

    const summary = {
      callSid: call.parameters?.['CallSid'] ?? 'unknown',
      durationSec,
      codec: samples[samples.length - 1]?.codecName,
      avgJitterMs: avg((s) => s.jitter),
      maxJitterMs: max((s) => s.jitter),
      avgRttMs: avg((s) => s.rtt),
      maxRttMs: max((s) => s.rtt),
      avgPacketsLostFractionPct: avg((s) => s.packetsLostFraction * 100),
      totalPacketsLost: samples.reduce((sum, s) => sum + s.packetsLost, 0),
      avgMos: mosValues.length
        ? Math.round((mosValues.reduce((a, b) => a + b, 0) / mosValues.length) * 100) / 100
        : null,
      minMos: mosValues.length ? Math.min(...mosValues) : null,
      warningCount: this.callWarnings.length,
      warnings: this.callWarnings,
    };

    console.log(`📊 [CallSummary] Call ${summary.callSid} performance report`);
    console.table({
      'Duration (s)': summary.durationSec,
      Codec: summary.codec,
      'Avg Jitter (ms)': summary.avgJitterMs,
      'Max Jitter (ms)': summary.maxJitterMs,
      'Avg RTT (ms)': summary.avgRttMs,
      'Max RTT (ms)': summary.maxRttMs,
      'Avg Packet Loss (%)': summary.avgPacketsLostFractionPct,
      'Total Packets Lost': summary.totalPacketsLost,
      'Avg MOS (1-4.5)': summary.avgMos,
      'Min MOS (1-4.5)': summary.minMos,
      'Warning Count': summary.warningCount,
    });
    if (summary.warnings.length) {
      console.log('⚠️ Warnings during call:', summary.warnings);
    }
  }

  disconnectCall() {
    this.device?.disconnectAll();
    localStorage.setItem('callActive', 'false');
    this.activeCall = undefined;
    this.activeCall$.next(undefined);
  }

  destroy() {
    this.device?.destroy();
    localStorage.setItem('callActive', 'false');
    this.device$.next(undefined);
    this.activeCall = undefined;
    this.activeCall$.next(undefined);
  }
}
